<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { keycloak } from "../main.ts";
import { useI18n } from "vue-i18n";
import { ProfileAdministrationAPI } from "../api";
import UserMenu from "@persolog/ui/UserMenu.vue";
import { Trainer } from "../types/PersologOnlineTypes.ts";

const { t } = useI18n();
const authenticated = computed(() => keycloak.authenticated || false);
const firstName = computed(() => keycloak.tokenParsed?.given_name || "");
const trainer = ref<Trainer>();

const portrait = computed(() => trainer.value?.portrait ?? "");

// admin's menu is just logout; logged-out, the login button logs in directly
// (loginOpensMenu=false below), so no logged-out menu items are needed.
const items = computed(() =>
    authenticated.value
        ? [
              {
                  label: t("header.logoutAdmin"),
                  icon: "pi pi-sign-out",
                  command: () => logout(),
              },
          ]
        : [],
);

// Methods
const logout = () => {
    keycloak.logout();
};

const login = () => {
    keycloak.login();
};

async function getTrainer() {
    const trainerId = keycloak.tokenParsed?.sub;
    if (!trainerId) {
        return;
    }

    try {
        trainer.value = await ProfileAdministrationAPI.fetchTrainer(trainerId);
    } catch (error) {
        // Some test flows authenticate before trainer bootstrap has propagated.
        // Keep avatar in skeleton state instead of throwing an unhandled rejection.
        console.warn("Trainer profile could not be loaded", error);
        trainer.value = undefined;
    }
}

onMounted(() => {
    if (keycloak.authenticated) {
        getTrainer();
    }
});
</script>

<template>
    <div class="flex flex-row flex-wrap">
        <div class="flex items-center justify-center">
            <UserMenu
                :authenticated="authenticated"
                :trainer="trainer ?? null"
                :portrait="portrait"
                :first-name="firstName"
                :items="items"
                menu-id="user-account_btn_menu"
                :login-opens-menu="false"
                @login="login"
            />
        </div>
    </div>
</template>
